import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getLatestMembers } from "../../../../../services/dashboardServices";
import { getReglementInscriptionByIdAndSaison } from "../../../../../services/reglementinscriptionServices";

import {
  FaRedo,
  FaUserClock,
  FaCalendarAlt,
  FaMousePointer,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaSpinner,
} from "react-icons/fa";

import ReglementInscriptionDrawer from "./ReglementInscriptionDrawer";

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

const DEBUG_LATEST_MEMBERS = true;

function debugLog(...args) {
  if (DEBUG_LATEST_MEMBERS) {
    console.log(...args);
  }
}

function debugWarn(...args) {
  if (DEBUG_LATEST_MEMBERS) {
    console.warn(...args);
  }
}

function debugError(...args) {
  if (DEBUG_LATEST_MEMBERS) {
    console.error(...args);
  }
}

function extractArray(data) {
  debugLog("[LatestMembers] extractArray() - data reçue :", data);

  if (Array.isArray(data)) {
    debugLog("[LatestMembers] extractArray() - data est déjà un tableau");
    return data;
  }

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) {
      debugLog(
        "[LatestMembers] extractArray() - tableau trouvé dans data.rows",
      );
      return data.rows;
    }

    if (Array.isArray(data.items)) {
      debugLog(
        "[LatestMembers] extractArray() - tableau trouvé dans data.items",
      );
      return data.items;
    }

    if (Array.isArray(data.members)) {
      debugLog(
        "[LatestMembers] extractArray() - tableau trouvé dans data.members",
      );
      return data.members;
    }

    if (Array.isArray(data.adherents)) {
      debugLog(
        "[LatestMembers] extractArray() - tableau trouvé dans data.adherents",
      );
      return data.adherents;
    }

    if (Array.isArray(data.data)) {
      debugLog(
        "[LatestMembers] extractArray() - tableau trouvé dans data.data",
      );
      return data.data;
    }
  }

  debugWarn(
    "[LatestMembers] extractArray() - aucun tableau détecté, retour []",
  );

  return [];
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .replace("Cours d’essai", "Cours d'essai");
}

function getStatusClasses(status) {
  switch (normalizeStatus(status)) {
    case "Licencié":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
    case "Licencié Hors Club":
      return "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20";
    case "Inscrit":
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
    case "Cours d'essai":
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
    case "Non Licencié - Arrêt":
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
    case "Ancien Licencié":
      return "bg-slate-900 text-white ring-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:ring-slate-100";
    default:
      return "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20";
  }
}

function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

function fallbackPhoto(genre) {
  return genre === "Féminin"
    ? "/img/judokate_avatar.png"
    : "/img/judoka_avatar.png";
}

function getPhoto(adherent) {
  const photo =
    adherent?.photo_url ||
    adherent?.photoURL ||
    adherent?.photo ||
    adherent?.image_url ||
    "";

  if (!photo || photo === "/img/default_avatar.png") {
    return fallbackPhoto(adherent?.genre);
  }

  return toAbsoluteUrl(photo);
}

function getDisplayName(adherent) {
  const nom = String(adherent?.nom || "")
    .trim()
    .toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

function formatDateTime(dateValue) {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function getGroups(adherent) {
  if (Array.isArray(adherent?.Groups)) {
    return adherent.Groups.map((group) => ({
      id: group?.id,
      nom: group?.nom,
    })).filter((group) => group.nom);
  }

  if (Array.isArray(adherent?.groups)) {
    return adherent.groups
      .map((group) => ({
        id: group?.id,
        nom: group?.nom || group?.name,
      }))
      .filter((group) => group.nom);
  }

  if (adherent?.group_name) return [{ nom: adherent.group_name }];
  if (adherent?.groupe_nom) return [{ nom: adherent.groupe_nom }];

  return [];
}

// --- Ajout : identifiant adhérent + saison courante, réutilisés pour la vérification règlement ---
function getAdherentId(adherent) {
  return (
    adherent?.id ||
    adherent?.adherent_id ||
    adherent?.adherentId ||
    null
  );
}

function getCurrentSeason() {
  const now = new Date();
  const year = now.getFullYear();
  const seasonStart = new Date(`${year}-08-01T00:00:00`);

  if (now >= seasonStart) {
    return `${year}-${year + 1}`;
  }

  return `${year - 1}-${year}`;
}

// --- Ajout : petite pastille indiquant le statut du règlement d'inscription ---
function ReglementBadge({ status }) {
  const config = {
    loading: {
      icon: <FaSpinner className="animate-spin" />,
      className:
        "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300",
      title: "Vérification du règlement d'inscription...",
    },
    yes: {
      icon: <FaCheckCircle />,
      className:
        "bg-emerald-500 text-white ring-2 ring-white dark:ring-slate-950",
      title: "Règlement d'inscription déjà affecté",
    },
    no: {
      icon: <FaExclamationTriangle />,
      className:
        "bg-amber-500 text-white ring-2 ring-white dark:ring-slate-950",
      title: "Aucun règlement d'inscription pour la saison en cours",
    },
    error: {
      icon: <FaQuestionCircle />,
      className:
        "bg-slate-400 text-white ring-2 ring-white dark:ring-slate-950",
      title: "Statut du règlement inconnu (erreur de vérification)",
    },
  };

  const current = config[status] || config.loading;

  return (
    <span
      title={current.title}
      className={`absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-[11px] shadow-sm ${current.className}`}
    >
      {current.icon}
    </span>
  );
}

function MemberCard({ adherent, onOpen, reglementStatus }) {
  const name = getDisplayName(adherent);
  const photo = getPhoto(adherent);
  const status = normalizeStatus(adherent?.statut_inscription);
  const groups = getGroups(adherent);
  const createdAt = adherent?.createdAt || adherent?.created_at || null;

  if (DEBUG_LATEST_MEMBERS) {
    console.log("[LatestMembers] rendu MemberCard :", {
      id: adherent?.id,
      adherent_id: adherent?.adherent_id,
      name,
      status,
      groups,
      createdAt,
      reglementStatus,
      adherent,
    });
  }

  return (
    <button
      type="button"
      onClick={() => onOpen?.(adherent)}
      className="group flex min-h-[118px] overflow-hidden rounded-3xl border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-[#013E8E]/15 dark:border-slate-800 dark:bg-slate-950/60 dark:hover:border-indigo-500/50"
      title="Ouvrir la préparation du règlement d'inscription"
    >
      <div className="relative h-auto w-24 shrink-0 overflow-hidden sm:w-28">
        <img
          src={photo}
          alt={name}
          loading="lazy"
          className="h-full min-h-[118px] w-full object-cover transition duration-300 group-hover:scale-105"
          onError={(event) => {
            debugWarn("[LatestMembers] erreur image adhérent :", {
              name,
              photo,
              adherent,
            });

            event.currentTarget.src = fallbackPhoto(adherent?.genre);
          }}
        />

        {/* Ajout : pastille statut règlement d'inscription */}
        <ReglementBadge status={reglementStatus} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="line-clamp-2 text-sm font-bold leading-5 text-slate-900 dark:text-white">
              {name}
            </h4>

            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-[11px] text-indigo-700 opacity-0 transition group-hover:opacity-100 dark:bg-indigo-500/10 dark:text-indigo-300">
              <FaMousePointer />
            </span>
          </div>

          <p className="mt-1 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <FaCalendarAlt className="shrink-0 text-[10px]" />
            <span className="truncate">{formatDateTime(createdAt)}</span>
          </p>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {status ? (
            <span
              className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${getStatusClasses(
                status,
              )}`}
              title={status}
            >
              <span className="truncate">{status}</span>
            </span>
          ) : null}

          {groups.length > 0 ? (
            <span
              className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
              title={groups.map((group) => group.nom).join(", ")}
            >
              <span className="truncate">
                {groups
                  .slice(0, 2)
                  .map((group) => group.nom)
                  .join(", ")}
              </span>
              {groups.length > 2 ? ` +${groups.length - 2}` : ""}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
              Sans groupe
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function SkeletonCard() {
  return (
    <div className="flex min-h-[118px] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/60">
      <div className="h-auto w-24 shrink-0 animate-pulse bg-slate-200 dark:bg-slate-800 sm:w-28" />

      <div className="min-w-0 flex-1 space-y-2 p-3">
        <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="mt-4 flex gap-2">
          <div className="h-5 w-20 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-5 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <FaUserClock />
      </div>

      <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-white">
        Aucun adhérent récent
      </h3>

      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Aucun nouvel adhérent n’a été trouvé pour le moment.
      </p>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-bold">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
      >
        <FaRedo />
        Réessayer
      </button>
    </div>
  );
}

export default function LatestMembers() {
  const [items, setItems] = useState([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // --- Ajout : map { adherentId: "loading" | "yes" | "no" | "error" } ---
  const [reglementMap, setReglementMap] = useState({});

  const fetchLatestMembers = async () => {
    console.groupCollapsed("[LatestMembers] fetchLatestMembers()");
    console.time("[LatestMembers] durée fetchLatestMembers");

    try {
      setLoading(true);
      setErrorMsg("");

      debugLog("[LatestMembers] appel getLatestMembers()...");

      const response = await getLatestMembers();

      debugLog("[LatestMembers] réponse brute getLatestMembers :", response);

      const list = extractArray(response);

      debugLog("[LatestMembers] tableau extrait :", list);
      debugLog("[LatestMembers] nombre extrait :", list.length);

      if (DEBUG_LATEST_MEMBERS && list.length > 0) {
        console.table(
          list.map((member) => ({
            id: member?.id,
            adherent_id: member?.adherent_id,
            nom: member?.nom,
            prenom: member?.prenom,
            statut_inscription: member?.statut_inscription,
            createdAt: member?.createdAt || member?.created_at,
            groupsCount: Array.isArray(member?.Groups)
              ? member.Groups.length
              : Array.isArray(member?.groups)
                ? member.groups.length
                : 0,
          })),
        );
      }

      const normalized = list.map((member) => ({
        ...member,
        Groups: Array.isArray(member?.Groups)
          ? member.Groups.map((group) => ({
              id: group?.id,
              nom: group?.nom,
            })).filter((group) => group.nom)
          : [],
      }));

      debugLog("[LatestMembers] tableau normalisé :", normalized);
      debugLog("[LatestMembers] nombre normalisé :", normalized.length);

      if (DEBUG_LATEST_MEMBERS && normalized.length > 0) {
        console.table(
          normalized.map((member) => ({
            id: member?.id,
            adherent_id: member?.adherent_id,
            nom: member?.nom,
            prenom: member?.prenom,
            groups: Array.isArray(member?.Groups)
              ? member.Groups.map((group) => group.nom).join(", ")
              : "",
          })),
        );
      }

      debugLog("[LatestMembers] tableau final complet :", normalized);
      debugLog("[LatestMembers] nombre total reçu :", normalized.length);

      setItems(normalized);
      setVisibleCount(10);
    } catch (error) {
      debugError(
        "[LatestMembers] erreur lors du chargement des derniers adhérents :",
        error,
      );

      setItems([]);
      setErrorMsg("Impossible de récupérer les derniers adhérents inscrits.");
    } finally {
      setLoading(false);
      console.timeEnd("[LatestMembers] durée fetchLatestMembers");
      console.groupEnd();
    }
  };

  useEffect(() => {
    debugLog(
      "[LatestMembers] useEffect initial - lancement fetchLatestMembers()",
    );
    fetchLatestMembers();
  }, []);

  useEffect(() => {
    debugLog("[LatestMembers] état items mis à jour :", {
      loading,
      errorMsg,
      itemsLength: items.length,
      items,
    });
  }, [items, loading, errorMsg]);

  // --- Ajout : vérification du règlement d'inscription pour chaque adhérent chargé ---
  const checkReglementsForItems = useCallback(async (list) => {
    if (!Array.isArray(list) || list.length === 0) {
      setReglementMap({});
      return;
    }

    const saison = getCurrentSeason();

    // Passe tous les adhérents visibles en "loading" le temps de la vérification
    setReglementMap((prev) => {
      const next = { ...prev };
      list.forEach((item) => {
        const id = getAdherentId(item);
        if (id) next[id] = "loading";
      });
      return next;
    });

    const results = await Promise.allSettled(
      list.map(async (item) => {
        const id = getAdherentId(item);
        if (!id) return null;

        try {
          const reglement = await getReglementInscriptionByIdAndSaison(
            id,
            saison,
          );

          return { id, status: reglement ? "yes" : "no" };
        } catch (error) {
          debugWarn(
            "[LatestMembers] erreur vérification règlement pour adhérent :",
            id,
            error,
          );
          return { id, status: "error" };
        }
      }),
    );

    setReglementMap((prev) => {
      const next = { ...prev };
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          next[result.value.id] = result.value.status;
        }
      });
      return next;
    });
  }, []);

  useEffect(() => {
    checkReglementsForItems(items);
  }, [items, checkReglementsForItems]);

  const latestDate = useMemo(() => {
    const first = items[0];

    debugLog("[LatestMembers] latestDate recalculée :", {
      first,
      latestDate: first?.createdAt || first?.created_at || null,
    });

    return first?.createdAt || first?.created_at || null;
  }, [items]);

  const displayedItems = useMemo(() => {
    return items.slice(0, visibleCount);
  }, [items, visibleCount]);

  const canShowMore = visibleCount < items.length;
  const canShowLess = visibleCount > 10;

  const showMore = () => {
    setVisibleCount((prev) => Math.min(prev + 10, items.length));
  };

  const showLess = () => {
    setVisibleCount((prev) => Math.max(prev - 10, 10));
  };

  const openDrawer = (adherent) => {
    debugLog("[LatestMembers] clic card adhérent :", adherent);

    setSelectedAdherent(adherent);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    debugLog("[LatestMembers] fermeture drawer");

    setDrawerOpen(false);
    setSelectedAdherent(null);
  };

  debugLog("[LatestMembers] rendu composant :", {
    loading,
    errorMsg,
    itemsLength: items.length,
    drawerOpen,
    selectedAdherent,
  });

  if (errorMsg) {
    debugWarn("[LatestMembers] rendu ErrorPanel :", errorMsg);

    return <ErrorPanel message={errorMsg} onRetry={fetchLatestMembers} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <FaUserClock />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Derniers adhérents inscrits
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Cliquez sur une fiche pour préparer son règlement d'inscription.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            {loading
              ? "Chargement..."
              : `${displayedItems.length} / ${items.length} affiché${
                  displayedItems.length > 1 ? "s" : ""
                }`}
          </span>

          {latestDate ? (
            <span className="hidden rounded-2xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-bold text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300 sm:inline-flex">
              Dernier : {formatDateTime(latestDate)}
            </span>
          ) : null}

          <button
            type="button"
            onClick={fetchLatestMembers}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaRedo className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      {loading ? (
        <>
          {debugLog("[LatestMembers] rendu SkeletonCard")}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[...Array(10)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        </>
      ) : items.length === 0 ? (
        <>
          {debugWarn("[LatestMembers] rendu EmptyState car items.length === 0")}
          <EmptyState />
        </>
      ) : (
        <>
          {debugLog("[LatestMembers] rendu grille cards :", items.length)}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {displayedItems.map((adherent, index) => {
              const adherentId = getAdherentId(adherent);

              return (
                <MemberCard
                  key={adherentId || index}
                  adherent={adherent}
                  onOpen={openDrawer}
                  reglementStatus={reglementMap[adherentId] || "loading"}
                />
              );
            })}
          </div>
        </>
      )}

      {items.length > 10 ? (
        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-center">
          {canShowLess ? (
            <button
              type="button"
              onClick={showLess}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaChevronUp />
              Voir moins
            </button>
          ) : null}

          {canShowMore ? (
            <button
              type="button"
              onClick={showMore}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-800"
            >
              <FaChevronDown />
              Voir 10 de plus
            </button>
          ) : null}
        </div>
      ) : null}

      <ReglementInscriptionDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        adherent={selectedAdherent}
        onCreated={() => {
          debugLog("[LatestMembers] onCreated drawer - refresh liste");
          fetchLatestMembers();
        }}
      />
    </div>
  );
}